import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    signal,
    computed,
    effect, OnInit,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { DynamicChartsComponent } from 'app/ui/dynamic-charts/dynamic-charts.component';
import { FormattingPipe } from 'app/modules/shared/services/formatting-data.pipe';
import { AuthSrcDirective } from 'app/modules/shared/directives/auth-src.directive';
import { ReportService } from '../../reports.service';
import { ChatService } from 'app/company/ai-chat/agent-chat/chat.service';
import { NewTrackerService } from 'app/company/tracker/new-tracker.service';
import { UserService } from 'app/core/user/user.service';
import { DeleteConfirmationComponent } from 'app/ui/delete-confirmation/delete-confirmation.component';
import { SendReportComponent } from '../send-report/send-report.component';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { CopyOnDblclickDirective } from '@fuse/directives/copy-on-dblclick.directive';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FilterNewTrackerComponent } from 'app/company/tracker/component/filter-new-tracker/filter-new-tracker.component';
import { ReportDataTableListComponent } from 'app/ui/report-data-table-list/report-data-table-list.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MarkdownService } from 'app/modules/shared/services/markdown.service';
import { MatDividerModule } from '@angular/material/divider';
// -------------------- Types --------------------
type ReportType = 'questionnaire' | 'tracker' | 'message' | 'legacy';

interface ChartBlock {
    id?: string;
    trackerId?: number | string;
    title?: string;
    xAxis?: string;
    yAxis?: string;
    series?: any;
    labels?: any;
    data?: any;
    appliedFilters?: { column: string; values: string[] }[];
    appliedStartDate?: string | null;
    appliedEndDate?: string | null;
}

/** One report “step” per API query: multiple charts stacked, one shared table (new message/tracker/questionnaire reports). */
export interface ReportQueryStep {
    chartsList: ChartBlock[];
    stepTitle?: string | null;
    trackerId?: number | string;
    /** First chart id (save title / delete / ignore query). */
    id?: string | number;
}

interface NormalizedReport {
    id: string;
    ReportType: ReportType;
    title: string;
    introduction: string;
    analysis: string;
    insights: string;
    conclusion: string;
    featured_image?: string;
    created?: string | Date;
    /** Legacy: one ChartBlock per slide. New: one ReportQueryStep per query (chartsList + stepTitle). */
    chart_data: Array<ChartBlock | ReportQueryStep>;
    data_table: Array<Record<string, any>[]>;
    tablesName: (string | null)[];
}

@Component({
    selector: 'app-view-report',
    standalone: true,

    imports: [
        CommonModule,
        TranslocoModule,
        MatIconModule,
        NgApexchartsModule,
        MatTooltipModule,
        FormattingPipe,
        FormsModule,
        ReactiveFormsModule,
        MatInputModule,
        MatFormFieldModule,
        MatButtonModule,
        DynamicChartsComponent,
        ScrollingModule,
        CopyOnDblclickDirective,
        ClipboardModule,
        MatDividerModule,
        AuthSrcDirective,
    ],
    templateUrl: './view-report.component.html',
    styleUrls: ['./view-report.component.scss'],
    providers: [DecimalPipe, DatePipe],
})
export class ViewReportComponent implements OnInit {
    // ───────────────────────── DI
    private route = inject(ActivatedRoute);
    private reportService = inject(ReportService);
    private chatService = inject(ChatService);
    private trackerService = inject(NewTrackerService);
    private userService = inject(UserService);
    private dialog = inject(MatDialog);
    private alert = inject(CustomAlertService);
    private transloco = inject(TranslocoService);
    private cdr = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);
    protected router = inject(Router);
    private sanitizer = inject(DomSanitizer);
    private markdownService = inject(MarkdownService);

    // ───────────────────────── UI state (signals)
    isGeneratingPDF = signal(false);
    isEditMode = signal(false);
    lang = signal<'en' | 'ar'>('en');
    companyImage = signal<string>('');
    reportId = signal<string>('');
    secondLevelFrom = signal<'questionnaire' | 'tracker' | 'message' | ''>('');
    isNewReport = signal<boolean>(false);

    currentChartIndex = signal(0);
    currentTableIndex = signal(0);
    showChart = signal(true);

    // Forms
    titleCtrl = new FormControl<string>('', { nonNullable: true });
    introductionCtrl = new FormControl<string>('', { nonNullable: true });
    analysisCtrl = new FormControl<string>('', { nonNullable: true });
    insightsCtrl = new FormControl<string>('', { nonNullable: true });
    conclusionCtrl = new FormControl<string>('', { nonNullable: true });
    chartCtrl = new FormControl<string>('', { nonNullable: true });

    // Data
    report = signal<NormalizedReport | null>(null);

    sanitize(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    markDownContent(value: string): string {
        if (!value) return '';
        return this.markdownService.parseMarkdown(value);
    }

    // Derived - one step per query (new reports); legacy = one chart block per slide
    hasMultipleCharts = computed(
        () => (this.report()?.chart_data?.length ?? 0) > 1
    );
    currentStep = computed(() => {
        const r = this.report();
        return r ? r.chart_data[this.currentChartIndex()] : undefined;
    });
    currentTable = computed(() => {
        const r = this.report();
        return r ? r.data_table[this.currentTableIndex()] : undefined;
    });

    /** True when the report has at least one non-empty data table (matches list `hasData`). */
    hasReportData = computed(() => {
        const tables = this.report()?.data_table;
        if (!Array.isArray(tables) || tables.length === 0) {
            return false;
        }
        return tables.some(rows => Array.isArray(rows) && rows.length > 0);
    });

    /** True when the current step has at least one chart `app-dynamic-charts` can render. */
    hasValidChartData = computed(() => {
        const step = this.currentStep();
        if (!step) return false;
        if (this.isReportQueryStep(step)) {
            return step.chartsList.some(c =>
                this.chartBlockHasRenderableSeries(c)
            );
        }
        return this.chartBlockHasRenderableSeries(step as ChartBlock);
    });

    /** No API charts for this query - show “Chart not found” placeholder (table may still show). */
    showChartNotFoundPlaceholder = computed(() => {
        const step = this.currentStep();
        if (!step || !this.isReportQueryStep(step)) return false;
        return step.chartsList.length === 0;
    });

    // Error messages per chart index (for empty results after filter/date)
    invalidMessages: Record<number, string> = {};

    // ───────────────────────── NEW: Collapsible hero image
    showHero = signal(true);

    toggleHero() {
        this.showHero.set(!this.showHero());
    }

    // ───────────────────────── NEW: Per-table expand/collapse
    private NEW_DEFAULT_COLLAPSED_HEIGHT = 360; // px
    private LEGACY_DEFAULT_COLLAPSED_HEIGHT = 360; // px

    expandedNewTables = signal<Record<number, boolean>>({});
    expandedLegacyTables = signal<Record<number, boolean>>({});
    collapsedHeights = signal<Record<string, number>>({}); // key: 'new:index' | 'legacy:index'

    isNewTableExpanded = (i: number) => !!this.expandedNewTables()[i];
    toggleNewTable(i: number) {
        const before = this.expandedNewTables();
        const map = { ...before };
        map[i] = !map[i];
        this.expandedNewTables.set(map);
        this.cdr.markForCheck();

        const key = `new:${i}`;
        if (!map[i] && this.collapsedHeights()[key] == null) {
            this.collapsedHeights.set({
                ...this.collapsedHeights(),
                [key]: this.NEW_DEFAULT_COLLAPSED_HEIGHT,
            });
        }
    }

    isLegacyTableExpanded = (i: number) => !!this.expandedLegacyTables()[i];
    toggleLegacyTable(i: number) {
        const before = this.expandedLegacyTables();
        const map = { ...before };
        map[i] = !map[i];
        this.expandedLegacyTables.set(map);
        this.cdr.markForCheck();

        const key = `legacy:${i}`;
        if (!map[i] && this.collapsedHeights()[key] == null) {
            this.collapsedHeights.set({
                ...this.collapsedHeights(),
                [key]: this.LEGACY_DEFAULT_COLLAPSED_HEIGHT,
            });
        }
    }

    getCollapsedHeight(i: number, type: 'new' | 'legacy' = 'new'): number {
        const key = `${type}:${i}`;
        return (
            this.collapsedHeights()[key] ??
            (type === 'new'
                ? this.NEW_DEFAULT_COLLAPSED_HEIGHT
                : this.LEGACY_DEFAULT_COLLAPSED_HEIGHT)
        );
    }

    // ───────────────────────── Lifecycle
    constructor(
        private _translocoService: TranslocoService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
        // Observe route params + language + company image together
        combineLatest([
            this.route.paramMap,
            this.transloco.langChanges$,
            this.userService.getcompany(),
        ])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(([params, lang, company]) => {
                    const id = params.get('reportId') ?? '';
                    this.reportId.set(id);
                    this.isEditMode.set((params.get('edit') ?? '') === 'true');
                    this.lang.set((lang as any) ?? 'en');
                    this.companyImage.set(company?.image ?? '');

                    if (!id) return of(null);

                    return this.reportService
                        .getById(id)
                        .pipe(switchMap(res => this.normalizeReport(res)));
                })
            )
            .subscribe((normalized) => {
                if (!normalized) return;
                this.report.set(normalized);
                this.isNewReport.set(
                    ['questionnaire', 'tracker', 'message'].includes(
                        normalized.ReportType
                    )
                );
                // initialize form values
                this.titleCtrl.setValue(normalized.title ?? '');
                this.introductionCtrl.setValue(normalized.introduction ?? '');
                this.analysisCtrl.setValue(normalized.analysis ?? '');
                this.insightsCtrl.setValue(normalized.insights ?? '');
                this.conclusionCtrl.setValue(normalized.conclusion ?? '');

                // set current chart title
                const first = normalized.chart_data?.[0];
                this.chartCtrl.setValue(this.getStepTitle(first));
                this.currentChartIndex.set(0);
                this.currentTableIndex.set(0);
                this.cdr.markForCheck();
            });

        // Fix Apex fill URLs once (safer than assigning on window)
        effect(() => {
            // no-op placeholder; if you still need URL fix, hook in DynamicChartsComponent itself.
        });
    }
    ngOnInit() {
        this._translocoService.langChanges$.subscribe((res: any) => {
            this.lang.set(res);
            this._changeDetectorRef.markForCheck();
        });
    }
    // ───────────────────────── Normalization
    private normalizeReport(raw: any) {
        const type: ReportType =
            raw?.ReportType === 'questionnaire'
                ? 'questionnaire'
                : raw?.ReportType === 'tracker'
                ? 'tracker'
                : !raw?.chart_data &&
                  !raw?.data_table &&
                  Array.isArray(raw?.chatIds) &&
                  raw.chatIds.length > 0
                ? 'message'
                : 'legacy';

        if (type === 'questionnaire') {
            this.secondLevelFrom.set('questionnaire');
            return this.fromQuestionnaires(raw);
        }

        if (type === 'tracker') {
            this.secondLevelFrom.set('tracker');
            return this.fromTrackers(raw);
        }

        if (type === 'message') {
            this.secondLevelFrom.set('message');
            return this.fromMessages(raw);
        }

        // legacy or already materialized chart_data/data_table
        const normalized: NormalizedReport = {
            id: raw?.id ?? raw?._id ?? '',
            ReportType: 'legacy',
            title: raw?.title ?? '',
            introduction: raw?.introduction ?? '',
            analysis: raw?.analysis ?? '',
            insights: raw?.insights ?? '',
            conclusion: raw?.conclusion ?? '',
            featured_image: raw?.featured_image,
            created: raw?.created,
            chart_data: Array.isArray(raw?.chart_data)
                ? raw.chart_data
                : raw?.chart_data
                ? [
                      {
                          ...raw.chart_data,
                          data: raw.chart_data?.data ?? raw?.data_table,
                      },
                  ]
                : [],
            data_table:
                Array.isArray(raw?.data_table) &&
                Array.isArray(raw?.data_table[0])
                    ? raw.data_table
                    : Array.isArray(raw?.data_table) && raw?.chart_data
                    ? [raw.data_table]
                    : [],
            tablesName: raw?.tablesName ?? [],
        };

        return of(normalized);
    }

    /**
     * API may return `charts` or `chart`, as a single object or an array.
     * Chat message-chart shows every chart in `query.charts`; reports use the same: one chart_data row per chart.
     */
    private extractChartBlocksFromQuery(q: any): any[] {
        const c = q?.charts ?? q?.chart;
        if (c == null) {
            return [];
        }
        if (Array.isArray(c)) {
            return c.filter(Boolean);
        }
        return [c];
    }

    isReportQueryStep(
        step: ChartBlock | ReportQueryStep | null | undefined
    ): step is ReportQueryStep {
        return (
            step != null &&
            typeof step === 'object' &&
            Array.isArray((step as ReportQueryStep).chartsList)
        );
    }

    /**
     * Normalize every chart on a query; synthetic table→chart only when there is a single chart slot (matches chat behavior).
     */
    private normalizeChartListForQuery(
        q: any,
        rows: Record<string, any>[]
    ): ChartBlock[] {
        const rawCharts = this.extractChartBlocksFromQuery(q);
        const multi = rawCharts.length > 1;
        const list: ChartBlock[] = [];
        for (const raw of rawCharts) {
            let block = this.normalizeChartSeriesAndLabels(raw ?? {});
            if (raw?.data) {
                (block as any).parsedData = raw.data;
            }
            if (
                !this.chartBlockHasRenderableSeries(block) &&
                rows.length &&
                !multi
            ) {
                const syn = this.buildSyntheticChartFromTableRows(rows, q);
                if (syn) {
                    block = { ...block, ...syn };
                }
            }
            list.push(block);
        }
        return list;
    }

    private buildReportQueryStep(
        q: any,
        rows: Record<string, any>[],
        trackerId?: number | string
    ): ReportQueryStep {
        const chartsList = this.normalizeChartListForQuery(q, rows);
        const first = chartsList[0];
        return {
            chartsList,
            stepTitle: q?.title ?? null,
            trackerId: trackerId ?? q?.id ?? q?.trackerId,
            id: first?.id as any,
        };
    }

    getChartDisplayTitle(chart: ChartBlock | null | undefined): string {
        if (!chart) {
            return '';
        }
        if (chart.title) {
            return chart.title;
        }
        const x = chart.xAxis || '';
        const y = chart.yAxis || '';
        return x || y ? `${x} vs ${y}` : '';
    }

    getChartTitleForDynamic(chart: ChartBlock | null | undefined): string {
        if (!chart) {
            return '';
        }
        if (chart.title) {
            return chart.title;
        }
        if (chart.xAxis) {
            return chart.yAxis
                ? `${chart.xAxis} vs ${chart.yAxis}`
                : chart.xAxis;
        }
        return chart.yAxis ? `X-Axis vs ${chart.yAxis}` : 'Y-Axis';
    }

    /**
     * New payloads may nest x-axis categories under `series.labels`.
     * `app-dynamic-charts` expects `labels` on the chart root and `series` as series-group maps only.
     */
    private normalizeChartSeriesAndLabels(chart: any): ChartBlock {
        if (!chart || typeof chart !== 'object') {
            return chart as ChartBlock;
        }
        const out: any = { ...chart };
        const s = out.series;
        if (
            s &&
            typeof s === 'object' &&
            s !== true &&
            !Array.isArray(s) &&
            Array.isArray((s as any).labels)
        ) {
            const { labels: nestedLabels, ...seriesRest } = s as any;
            if (out.labels == null && nestedLabels != null) {
                out.labels = nestedLabels;
            }
            out.series = seriesRest;
        }
        return out as ChartBlock;
    }

    /**
     * Table rows may be `rows[]` or wrapped as `[rows[]]` (batch shape from API).
     */
    private normalizeQueryTableData(q: any): Record<string, any>[] {
        const d = q?.data;
        if (d == null || !Array.isArray(d) || d.length === 0) {
            return [];
        }
        const first = d[0];
        if (Array.isArray(first)) {
            return first as Record<string, any>[];
        }
        return d as Record<string, any>[];
    }

    /** True when `app-dynamic-charts` can render the block (or boolean raw flags). */
    private chartBlockHasRenderableSeries(chart: ChartBlock | null | undefined): boolean {
        if (!chart) {
            return false;
        }
        if (typeof (chart as any).series === 'boolean') {
            return true;
        }
        if (!chart.series || chart.labels == null) {
            return false;
        }
        const labelsArr = Array.isArray(chart.labels) ? chart.labels : [];
        if (labelsArr.length === 0) {
            return false;
        }
        if (typeof chart.series !== 'object') {
            return false;
        }
        return Object.keys(chart.series).some((key) => {
            const seriesArray = (chart.series as any)[key];
            return (
                Array.isArray(seriesArray) &&
                seriesArray.some(
                    (s: any) => Array.isArray(s?.data) && s.data.length > 0
                )
            );
        });
    }

    /**
     * When the API returns query `data` but no `charts`, build a simple bar chart so the report can render.
     */
    private buildSyntheticChartFromTableRows(
        rows: Record<string, any>[],
        q?: any
    ): ChartBlock | null {
        if (!Array.isArray(rows) || rows.length === 0) {
            return null;
        }
        const first = rows[0];
        if (!first || typeof first !== 'object') {
            return null;
        }
        const keys = Object.keys(first);
        if (keys.length === 0) {
            return null;
        }

        const coerceNum = (v: any): number => {
            if (v === '' || v == null) {
                return 0;
            }
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        };

        const humanize = (k: string) =>
            k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

        if (rows.length === 1 && keys.length === 1) {
            const k = keys[0];
            return {
                title: q?.title ?? humanize(k),
                labels: [humanize(k)],
                series: {
                    default: [{ name: k, type: 'bar', data: [coerceNum(first[k])] }],
                },
            };
        }

        if (rows.length === 1) {
            const labels = keys.map(humanize);
            const data = keys.map(k => coerceNum(first[k]));
            return {
                title: q?.title ?? 'Results',
                labels,
                series: {
                    default: [
                        {
                            name: q?.tableName || 'Value',
                            type: 'bar',
                            data,
                        },
                    ],
                },
            };
        }

        const catKey = keys[0];
        const valueKey =
            keys.slice(1).find(k => Number.isFinite(Number(first[k]))) ??
            keys[1] ??
            keys[0];
        const labels = rows.map(r => String(r[catKey] ?? ''));
        const data = rows.map(r => coerceNum(r[valueKey]));
        return {
            title: q?.title ?? 'Results',
            labels,
            series: {
                default: [
                    {
                        name: valueKey,
                        type: 'bar',
                        data,
                    },
                ],
            },
        };
    }

    private fromQuestionnaires(res: any) {
        const ids: string[] = res?.questionnaireIds ?? [];
        const ignore = (res?.IgnoreList ?? []).join(',');

        if (!ids.length) {
            return of(this.emptyNormalized(res, 'questionnaire'));
        }

        const calls = ids.map(id =>
            this.chatService.getQuestionareDataForReport(id, ignore)
        );
        return forkJoin(calls).pipe(
            map((results: any[]) => {
                const chart_data: Array<ChartBlock | ReportQueryStep> = [];
                const data_table: Array<Record<string, any>[]> = [];
                const tablesName: (string | null)[] = [];
                results.forEach((r) => {
                    (r?.queries ?? []).forEach((q: any) => {
                        const rows = this.normalizeQueryTableData(q);
                        chart_data.push(this.buildReportQueryStep(q, rows));
                        data_table.push(rows);
                        tablesName.push(q?.tableName ?? null);
                    });
                });
                return this.packNormalized(
                    res,
                    'questionnaire',
                    chart_data,
                    data_table,
                    tablesName
                );
            })
        );
    }

    private fromTrackers(res: any) {
        const ids: string[] = res?.trackerIds ?? [];
        if (!ids.length) {
            return of(this.emptyNormalized(res, 'tracker'));
        }
        const calls = ids.map(id =>
            this.trackerService.getTrackerDataForReport(id)
        );
        return forkJoin(calls).pipe(
            map((results: any[]) => {
                const chart_data: Array<ChartBlock | ReportQueryStep> = [];
                const data_table: Array<Record<string, any>[]> = [];
                const tablesName: (string | null)[] = [];

                results.forEach((response: any) => {
                    // Handle new API structure: response.data.trackerData.queries
                    const trackerData = response?.data?.trackerData;
                    const queries = trackerData?.queries ?? [];

                    queries.forEach((query: any) => {
                        const rows = this.normalizeQueryTableData(query);
                        chart_data.push(
                            this.buildReportQueryStep(
                                query,
                                rows,
                                trackerData?.id
                            )
                        );
                        data_table.push(rows);
                        tablesName.push(query?.tableName ?? null);
                    });
                });
                return this.packNormalized(
                    res,
                    'tracker',
                    chart_data,
                    data_table,
                    tablesName
                );
            })
        );
    }

    private fromMessages(res: any) {
        const ids: string[] = res?.chatIds ?? [];
        const ignore = (res?.IgnoreList ?? []).join(',');
        if (!ids.length) {
            return of(this.emptyNormalized(res, 'message'));
        }
        const calls = ids.map(id =>
            this.chatService.getMessageDataForReport(id, ignore)
        );
        return forkJoin(calls).pipe(
            map((results: any[]) => {
                const chart_data: Array<ChartBlock | ReportQueryStep> = [];
                const data_table: Array<Record<string, any>[]> = [];
                const tablesName: (string | null)[] = [];

                results.forEach((r: any) => {
                    (r?.queries ?? []).forEach((q: any) => {
                        let rows = this.normalizeQueryTableData(q);
                        if (!rows.length && r?.data != null) {
                            rows = this.normalizeQueryTableData({ data: r.data });
                        }

                        chart_data.push(this.buildReportQueryStep(q, rows));
                        data_table.push(rows);
                        tablesName.push(q?.tableName ?? null);
                    });
                });

                return this.packNormalized(
                    res,
                    'message',
                    chart_data,
                    data_table,
                    tablesName
                );
            })
        );
    }

    private emptyNormalized(res: any, type: ReportType): NormalizedReport {
        return {
            id: res?.id ?? res?._id ?? '',
            ReportType: type,
            title: res?.title ?? '',
            introduction: res?.introduction ?? '',
            analysis: res?.analysis ?? '',
            insights: res?.insights ?? '',
            conclusion: res?.conclusion ?? '',
            featured_image: res?.featured_image,
            created: res?.created,
            chart_data: [],
            data_table: [],
            tablesName: [],
        };
    }

    private packNormalized(
        res: any,
        type: ReportType,
        chart_data: Array<ChartBlock | ReportQueryStep>,
        data_table: Array<Record<string, any>[]>,
        tablesName: (string | null)[]
    ): NormalizedReport {
        const mergedCharts = chart_data.map((chart, idx) => {
            const rows = data_table[idx] ?? [];
            if (this.isReportQueryStep(chart)) {
                const nextList = chart.chartsList.map((c) => {
                    let c2 = { ...c };
                    if (
                        !this.chartBlockHasRenderableSeries(c2) &&
                        rows.length > 0 &&
                        chart.chartsList.length <= 1
                    ) {
                        const syn = this.buildSyntheticChartFromTableRows(
                            rows,
                            {}
                        );
                        if (syn) {
                            c2 = { ...c2, ...syn };
                        }
                    }
                    return c2;
                });
                const first = nextList[0];
                return {
                    ...chart,
                    chartsList: nextList,
                    id: first?.id ?? chart.id,
                } as ReportQueryStep;
            }
            let c = chart as ChartBlock;
            if (
                !this.chartBlockHasRenderableSeries(c) &&
                Array.isArray(rows) &&
                rows.length > 0
            ) {
                const syn = this.buildSyntheticChartFromTableRows(rows, {});
                if (syn) {
                    c = { ...c, ...syn };
                }
            }
            return c;
        });

        const validIndices: number[] = [];

        mergedCharts.forEach((chart, idx) => {
            if (this.isReportQueryStep(chart)) {
                validIndices.push(idx);
                return;
            }
            const hasTable =
                Array.isArray(data_table[idx]) && data_table[idx].length > 0;
            const hasRenderableChart =
                this.chartBlockHasRenderableSeries(chart as ChartBlock);
            const hasLegacyShape =
                chart &&
                (chart.data ||
                    (Object.keys(chart).length > 0 &&
                        (chart.xAxis || chart.yAxis || chart.title)));

            if (hasRenderableChart || hasTable || hasLegacyShape) {
                validIndices.push(idx);
            }
        });

        const filteredCharts = validIndices.map(idx => mergedCharts[idx]);
        const filteredTables = validIndices.map(idx => data_table[idx]);
        const filteredTableNames = validIndices.map(idx => tablesName[idx]);

        if (filteredCharts.length > 0) {
            filteredCharts.forEach((chart, idx) => {
                if (this.isReportQueryStep(chart)) {
                    return;
                }
                const c = chart as ChartBlock;
                if (!c.series && !c.labels) {
                    console.warn(
                        `Chart at index ${idx} is missing both series and labels`,
                        chart
                    );
                } else if (!c.series) {
                    console.warn(
                        `Chart at index ${idx} is missing series`,
                        chart
                    );
                } else if (!c.labels) {
                    console.warn(
                        `Chart at index ${idx} is missing labels`,
                        chart
                    );
                }
            });
        }

        return {
            id: res?.id ?? res?._id ?? '',
            ReportType: type,
            title: res?.title ?? '',
            introduction: res?.introduction ?? '',
            analysis: res?.analysis ?? '',
            insights: res?.insights ?? '',
            conclusion: res?.conclusion ?? '',
            featured_image: res?.featured_image,
            created: res?.created,
            chart_data: filteredCharts,
            data_table: filteredTables,
            tablesName: filteredTableNames,
        };
    }

    // ───────────────────────── UI helpers
    getStepTitle(step?: ChartBlock | ReportQueryStep | null): string {
        if (!step) return 'Chart';
        if (this.isReportQueryStep(step)) {
            const t = step.stepTitle?.trim();
            if (t) return t;
            const first = step.chartsList?.[0];
            return this.getChartTitle(first);
        }
        const c = step as ChartBlock;
        return (
            c?.title ||
            (c?.xAxis && c?.yAxis ? `${c.xAxis} vs ${c.yAxis}` : 'Chart')
        );
    }

    getChartTitle(chartData?: ChartBlock | ChartBlock[] | null): string {
        if (!chartData) return 'Chart';
        const c = Array.isArray(chartData) ? chartData[0] : chartData;
        return (
            c?.title ||
            (c?.xAxis && c?.yAxis ? `${c.xAxis} vs ${c.yAxis}` : 'Chart')
        );
    }

    chartRenderable(chart: ChartBlock | null | undefined): boolean {
        if (!chart) return false;
        return this.chartBlockHasRenderableSeries(chart);
    }

    nextChart() {
        const r = this.report();
        if (!r) return;

        // Handle case where there are more tables than charts
        const hasCharts = r.chart_data && r.chart_data.length > 0;
        const hasTables = r.data_table && r.data_table.length > 0;

        if (hasCharts && this.currentChartIndex() < r.chart_data.length - 1) {
            // Force component recreation by toggling visibility
            this.showChart.set(false);

            const next = this.currentChartIndex() + 1;
            this.currentChartIndex.set(next);

            // Validate chart before setting title
            const nextChart = r.chart_data[next];
            if (nextChart) {
                this.chartCtrl.setValue(this.getStepTitle(nextChart));
            }

            // Use longer delay to ensure DOM is fully cleared and data is ready
            setTimeout(() => {
                this.showChart.set(true);
                this.cdr.detectChanges();
            }, 100);
        }

        if (hasTables && this.currentTableIndex() < r.data_table.length - 1) {
            this.currentTableIndex.set(this.currentTableIndex() + 1);
        }
    }

    prevChart() {
        const r = this.report();
        if (!r) return;

        const hasCharts = r.chart_data && r.chart_data.length > 0;
        const hasTables = r.data_table && r.data_table.length > 0;

        if (hasCharts && this.currentChartIndex() > 0) {
            this.showChart.set(false);

            const prev = this.currentChartIndex() - 1;
            this.currentChartIndex.set(prev);

            const prevChart = r?.chart_data?.[prev];
            if (prevChart) {
                this.chartCtrl.setValue(this.getStepTitle(prevChart));
            }

            setTimeout(() => {
                this.showChart.set(true);
                this.cdr.detectChanges();
            }, 100);
        }

        if (hasTables && this.currentTableIndex() > 0) {
            this.currentTableIndex.set(this.currentTableIndex() - 1);
        }
    }

    trackByIndex = (_: number, __: unknown) => _;

    // ───────────────────────── Actions
    saveNewTitle() {
        const r = this.report();
        if (!r) return;
        const idx = this.currentChartIndex();
        const chart = r.chart_data[idx];
        const chartId = this.isReportQueryStep(chart)
            ? chart.id ?? chart.chartsList?.[0]?.id
            : chart?.id;
        if (!chartId) return;

        const modal = {
            chartId,
            reportType: r.ReportType,
            title: this.chartCtrl.value ?? '',
        };

        this.reportService
            .updateChartTitle(modal)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    const updated = structuredClone(r);
                    const cur = updated.chart_data[idx];
                    if (this.isReportQueryStep(cur)) {
                        updated.chart_data[idx] = {
                            ...cur,
                            stepTitle: this.chartCtrl.value ?? '',
                        };
                    } else {
                        updated.chart_data[idx] = {
                            ...cur,
                            title: this.chartCtrl.value ?? '',
                        } as ChartBlock;
                    }
                    this.report.set(updated);
                    this.cdr.detectChanges();
                },
                error: err =>
                    this.alert.failedMessage(
                        err?.error?.message ?? 'Failed to update chart title'
                    ),
            });
    }

    deleteChartAt(index: number) {
        const r = this.report();
        if (!r) return;

        const dialogRef = this.dialog.open(DeleteConfirmationComponent, {
            width: '30%',
            data: 'Delete this chart with its corresponding table',
            panelClass: 'cropperDialog',
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((confirmed) => {
                if (!confirmed) return;

                const chart = r.chart_data[index];
                const chartId = this.isReportQueryStep(chart)
                    ? chart.id ?? chart.chartsList?.[0]?.id
                    : chart?.id;
                if (!chartId) {
                    this.spliceChartAndTable(index);
                    return;
                }

                const modal = { chartId };
                this.reportService
                    .ignoreQueries(this.reportId(), modal)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => this.spliceChartAndTable(index),
                        error: err =>
                            this.alert.failedMessage(
                                err?.error?.message ?? 'Failed to delete chart'
                            ),
                    });
            });
    }

    private spliceChartAndTable(index: number) {
        const r = this.report();
        if (!r) return;
        const updated = structuredClone(r);
        updated.chart_data.splice(index, 1);
        updated.data_table.splice(index, 1);
        if (this.currentChartIndex() >= updated.chart_data.length) {
            this.currentChartIndex.set(
                Math.max(0, updated.chart_data.length - 1)
            );
        }
        if (this.currentTableIndex() >= updated.data_table.length) {
            this.currentTableIndex.set(
                Math.max(0, updated.data_table.length - 1)
            );
        }
        this.report.set(updated);
        this.cdr.markForCheck();
    }

    editReport() {
        const payload = {
            title: this.titleCtrl.value,
            introduction: this.introductionCtrl.value,
            analysis: this.analysisCtrl.value,
            insights: this.insightsCtrl.value,
            conclusion: this.conclusionCtrl.value,
        };

        this.reportService
            .editReport(this.reportId(), payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () =>
                    this.alert.successMessage(
                        'Report has been edited successfully'
                    ),
                error: err =>
                    this.alert.failedMessage(
                        err?.error?.message ?? 'Failed to edit report'
                    ),
            });
    }

    sendEmail() {
        this.dialog.open(SendReportComponent, {
            width: '60%',
            maxHeight: '60dvh',
            panelClass: 'bigDialog',
            data: { id: this.reportId() },
        });
    }

    // utils
    keysOf(obj?: Record<string, any>): string[] {
        return obj ? Object.keys(obj) : [];
    }

    currentTableLength(): number {
        const t = this.currentTable();
        return Array.isArray(t) ? t.length : 0;
    }

    currentAppliedStartDate(): string | null {
        const c = this.currentStep() as any;
        if (this.isReportQueryStep(c)) {
            return c.chartsList?.[0]?.appliedStartDate ?? null;
        }
        return c?.appliedStartDate || null;
    }

    currentAppliedEndDate(): string | null {
        const c = this.currentStep() as any;
        if (this.isReportQueryStep(c)) {
            return c.chartsList?.[0]?.appliedEndDate ?? null;
        }
        return c?.appliedEndDate || null;
    }

    currentInvalidMessage(): string | null {
        const idx = this.currentChartIndex();
        return this.invalidMessages[idx] || null;
    }

    // ───────────────────────── Per-chart filter & date (report context)
    onFilterChart(chart: ChartBlock): void {
        if (!chart?.trackerId && !(chart as any)?.id) return;
        const trackerId = chart.trackerId || (chart as any).id;
        this.trackerService
            .getTrackerDimensionsColumns(trackerId as any)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: any) => {
                const dialogRef = this.dialog.open(FilterNewTrackerComponent, {
                    width: '50%',
                    maxHeight: '60dvh',
                    data: {
                        dimensionColumns:
                            response?.data?.dimensionColumns || [],
                        filters:
                            chart?.appliedFilters ||
                            response?.data?.filters ||
                            [],
                        table: response?.data?.table,
                        isExternal: response?.data?.isExternal,
                        dataSourceId: response?.data?.dataSourceId,
                    },
                });

                dialogRef
                    .afterClosed()
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((result: any) => {
                        if (result?.selectedItems) {
                            const r = this.report();
                            if (!r) return;
                            const idx = this.currentChartIndex();
                            const updated = structuredClone(r);
                            const cur = updated.chart_data[idx];
                            const mapped = result.selectedItems.map(
                                (item: any) => ({
                                    column: item.columnName,
                                    values: item.item,
                                })
                            );
                            if (this.isReportQueryStep(cur)) {
                                const chartsList = [...(cur.chartsList || [])];
                                const first = chartsList[0]
                                    ? { ...chartsList[0] }
                                    : ({} as ChartBlock);
                                first.appliedFilters = mapped;
                                chartsList[0] = first;
                                updated.chart_data[idx] = {
                                    ...cur,
                                    chartsList,
                                };
                            } else {
                                (updated.chart_data[idx] as any).appliedFilters =
                                    mapped;
                            }
                            this.report.set(updated);
                            this.cdr.detectChanges();

                            this.refreshReportChart(
                                trackerId as any,
                                null,
                                null,
                                {
                                    selectedItems: result.selectedItems,
                                }
                            );
                        }
                    });
            });
    }

    onReportDateRange(evt: {
        startDate: Date | null;
        endDate: Date | null;
    }): void {
        const r = this.report();
        if (!r) return;
        const step = r.chart_data[this.currentChartIndex()];
        const trackerId = this.isReportQueryStep(step)
            ? step.trackerId
            : (step as any)?.trackerId || (step as any)?.id;
        if (!trackerId) return;

        const start = evt.startDate
            ? this.formatDateToDDMMYYYY(evt.startDate)
            : null;
        const end = evt.endDate ? this.formatDateToDDMMYYYY(evt.endDate) : null;
        const modal = {
            selectedItems: this.isReportQueryStep(step)
                ? step.chartsList?.[0]?.appliedFilters || []
                : (step as any).appliedFilters || [],
        };
        this.refreshReportChart(trackerId as any, start, end, modal);
    }

    openReportDataTable(chart: any): void {
        const r = this.report();
        if (!r) return;

        const idx = this.currentChartIndex();
        const tableData = this.currentTable();

        if (!tableData || tableData.length === 0) {
            this.alert.failedMessage(
                'There is no table data available for this chart.'
            );
            return;
        }

        const tableName = r.tablesName?.[idx] || 'Data Table';

        const dialogRef = this.dialog.open(ReportDataTableListComponent, {
            width: '70%',
            maxHeight: '90dvh',
            panelClass: 'biggDialog',
            data: {
                dataTable: tableData,
                from: 'report',
                name: tableName,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    /** Same dialog as the reports list "View Data" — all query tables for this report. */
    showDatatable(): void {
        const r = this.report();
        const id = this.reportId();
        if (!r || !id) {
            return;
        }

        this.reportService
            .getReportsData(id, r.ReportType)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res: { data?: { data_table?: unknown } }) => {
                const dataTable = res?.data?.data_table;
                if (!dataTable) {
                    return;
                }

                this.dialog.open(ReportDataTableListComponent, {
                    width: '70%',
                    maxHeight: '80dvh',
                    panelClass: 'biggDialog',
                    data: {
                        dataTable,
                        from: this.isArrayOfArrays(dataTable as unknown[])
                            ? 'new-report'
                            : 'newest-report',
                        name: r.title ?? '',
                    },
                });
            });
    }

    private isArrayOfArrays(arr: unknown[]): boolean {
        return (
            arr.length > 0 &&
            arr.every(item => Array.isArray(item) || item === null)
        );
    }

    private refreshReportChart(
        trackerId: number | string,
        start: string | null,
        end: string | null,
        modal?: any
    ): void {
        this.trackerService
            .getTrackerDataForReport(
                trackerId,
                start || undefined,
                end || undefined,
                modal
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response: any) => {
                // Handle new API structure: response.data.trackerData.queries[0]
                const trackerData = response?.data?.trackerData;
                const updatedQuery = trackerData?.queries?.[0];
                const idx = this.currentChartIndex();

                if (!updatedQuery) {
                    this.invalidMessages[idx] =
                        'This Tracker has no data available for the selected filter. Please try choosing a different filter.';
                    this.cdr.detectChanges();
                    return;
                }

                const r = this.report();
                if (!r) return;
                const updated = structuredClone(r);
                const prev = updated.chart_data[idx];
                const rows = this.normalizeQueryTableData(updatedQuery);
                const baseStep = this.buildReportQueryStep(
                    updatedQuery,
                    rows,
                    trackerData?.id
                );

                if (this.isReportQueryStep(prev)) {
                    const filterFromModal = modal?.selectedItems?.length
                        ? modal.selectedItems.map((x: any) => ({
                              column: x.columnName,
                              values: x.item,
                          }))
                        : undefined;
                    const mergedStep: ReportQueryStep = {
                        ...baseStep,
                        stepTitle:
                            baseStep.stepTitle ?? prev.stepTitle ?? null,
                        id: baseStep.id ?? prev.id,
                        chartsList: baseStep.chartsList.map((c, i) => {
                            const p = prev.chartsList?.[i];
                            return {
                                ...c,
                                appliedFilters:
                                    filterFromModal ??
                                    p?.appliedFilters ??
                                    c.appliedFilters,
                                appliedStartDate:
                                    start != null
                                        ? start
                                        : p?.appliedStartDate ??
                                          c.appliedStartDate ??
                                          null,
                                appliedEndDate:
                                    end != null
                                        ? end
                                        : p?.appliedEndDate ??
                                          c.appliedEndDate ??
                                          null,
                            };
                        }),
                    };
                    updated.chart_data[idx] = mergedStep;
                } else {
                    const nextChart = this.normalizeChartSeriesAndLabels(
                        updatedQuery.chart ?? {}
                    );
                    let merged: any = {
                        ...(prev as any),
                        ...nextChart,
                        trackerId: trackerData?.id,
                        title: nextChart?.title ?? (prev as any)?.title,
                        appliedFilters:
                            (modal?.selectedItems?.length
                                ? modal.selectedItems.map((x: any) => ({
                                      column: x.columnName,
                                      values: x.item,
                                  }))
                                : (prev as any)?.appliedFilters) ||
                            (prev as any)?.appliedFilters ||
                            [],
                        appliedStartDate:
                            start != null
                                ? start
                                : (prev as any)?.appliedStartDate || null,
                        appliedEndDate:
                            end != null
                                ? end
                                : (prev as any)?.appliedEndDate || null,
                    };
                    if (
                        !this.chartBlockHasRenderableSeries(merged) &&
                        rows.length
                    ) {
                        const syn = this.buildSyntheticChartFromTableRows(
                            rows,
                            updatedQuery
                        );
                        if (syn) {
                            merged = { ...merged, ...syn };
                        }
                    }
                    updated.chart_data[idx] = merged as any;
                }
                this.report.set(updated);
                delete this.invalidMessages[idx];
                this.cdr.detectChanges();
            });
    }

    private formatDateToDDMMYYYY(date: Date): string {
        const day = ('0' + date.getDate()).slice(-2);
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }
}
